import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, ArrowUpRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    topic: "general",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "", topic: "general" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-12 sm:py-16 border-t border-black/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#0066FF]">
            Contact
          </p>
          <h2 className="font-display mt-5 text-4xl sm:text-5xl font-bold tracking-[-0.02em] text-zinc-950 leading-[1.05]">
            Connect with us to build the future of professional learning.
          </h2>
          <p className="mt-6 text-lg text-zinc-600 font-light leading-relaxed max-w-md">
            For Vibecast Studio access, enterprise partnerships, investor
            conversations, press, or talent — drop us a note and we&apos;ll reply
            within two business days.
          </p>

          <div className="mt-8 flex flex-col gap-3 max-w-md">
            <a
              href="#contact-form"
              onClick={(e) => {
                e.preventDefault();
                setForm((f) => ({ ...f, topic: "waitlist" }));
                document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="contact-cta-waitlist"
              className="group inline-flex items-center justify-between gap-4 bg-[#0066FF] hover:bg-[#0052CC] text-white text-sm font-semibold pl-5 pr-4 h-14 rounded-2xl transition-all shadow-[0_10px_28px_-12px_rgba(0,102,255,0.55)]"
            >
              <span>Join the Vibecast Beta waitlist</span>
              <span className="grid place-items-center w-8 h-8 rounded-full bg-white/15 group-hover:bg-white/25 transition">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </a>
            <a
              href="#contact-form"
              onClick={(e) => {
                e.preventDefault();
                setForm((f) => ({ ...f, topic: "creator" }));
                document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="contact-cta-creator"
              className="group inline-flex items-center justify-between gap-4 bg-white border border-black/15 hover:border-zinc-950 text-zinc-950 text-sm font-semibold pl-5 pr-4 h-14 rounded-2xl transition-all"
            >
              <span>Become a Creator</span>
              <span className="grid place-items-center w-8 h-8 rounded-full bg-zinc-950 text-white">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </a>
            <a
              href="#contact-form"
              onClick={(e) => {
                e.preventDefault();
                setForm((f) => ({ ...f, topic: "partnership" }));
                document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid="contact-cta-partner"
              className="group inline-flex items-center justify-between gap-4 bg-white border border-black/15 hover:border-zinc-950 text-zinc-950 text-sm font-semibold pl-5 pr-4 h-14 rounded-2xl transition-all"
            >
              <span>Partner with LAIV</span>
              <span className="grid place-items-center w-8 h-8 rounded-full bg-zinc-950 text-white">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </a>
          </div>
        </div>

        <form
          onSubmit={submit}
          noValidate
          id="contact-form"
          data-testid="contact-form"
          className="col-span-12 lg:col-span-7 rounded-3xl border border-black/5 bg-zinc-50 p-7 sm:p-10"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="Your name"
              testid="contact-input-name"
              value={form.name}
              onChange={update("name")}
              placeholder="Ada Lovelace"
              required
            />
            <Field
              label="Email"
              testid="contact-input-email"
              type="email"
              value={form.email}
              onChange={update("email")}
              placeholder="you@company.com"
              required
            />
            <Field
              label="Company / Org"
              testid="contact-input-company"
              value={form.company}
              onChange={update("company")}
              placeholder="Optional"
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-600">
                Topic
              </label>
              <select
                value={form.topic}
                onChange={update("topic")}
                data-testid="contact-input-topic"
                className="h-11 rounded-xl border border-black/10 bg-white px-4 text-sm text-zinc-950 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/30 focus:border-[#0066FF] transition"
              >
                <option value="general">General</option>
                <option value="waitlist">Vibecast Beta Waitlist</option>
                <option value="creator">Become a Creator</option>
                <option value="partnership">Partnership</option>
                <option value="investor">Investor</option>
                <option value="press">Press</option>
                <option value="careers">Careers</option>
              </select>
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-1.5">
            <label className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-600">
              Message
            </label>
            <textarea
              required
              value={form.message}
              onChange={update("message")}
              data-testid="contact-input-message"
              rows={5}
              placeholder="Tell us what you're thinking about…"
              className="rounded-xl border border-black/10 bg-white px-4 py-3 text-sm text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/30 focus:border-[#0066FF] transition resize-none"
            />
          </div>

          <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
            <p className="text-xs text-zinc-500 max-w-xs leading-relaxed">
              By submitting, you agree that Laiv Ai may contact you about your
              inquiry.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="group inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] disabled:opacity-70 text-white text-sm font-medium pl-5 pr-4 h-12 rounded-full transition-all duration-300 shadow-[0_10px_28px_-10px_rgba(0,102,255,0.6)]"
            >
              {loading ? "Sending…" : "Send message"}
              <span className="grid place-items-center w-7 h-7 rounded-full bg-white/15 group-hover:bg-white/25 transition">
                {loading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
              </span>
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...rest }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-600">
      {label}
    </label>
    <input
      {...rest}
      data-testid={testid}
      className="h-11 rounded-xl border border-black/10 bg-white px-4 text-sm text-zinc-950 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0066FF]/30 focus:border-[#0066FF] transition"
    />
  </div>
);

export default Contact;
